/**
 * Extended Collection URL Parser
 * 
 * Parses filtered collection URLs with multiple filters:
 * - Brand × Type: /collections/samsung-french-door-refrigerators
 * - Type × Price: /collections/french-door-refrigerators-under-1000
 * - Color × Type: /collections/stainless-steel-refrigerators
 * - Brand × Type × Energy Star: /collections/samsung-french-door-refrigerators-energy-star
 * - Color × Type × Price: /collections/white-refrigerators-under-500
 * - Color × Brand × Type: /collections/white-samsung-french-door-refrigerators
 * - Color × Brand × Type × Price: /collections/white-samsung-french-door-refrigerators-under-3000
 * - Type × Use Case: /collections/mini-fridges-for-bedroom
 * - Energy Star Overall: /collections/energy-star
 */

import { allCanonicalSlugs } from './collections'
import { BRAND_RULES } from './brand-rules'
import { getUseCase } from './use-cases'

/**
 * Tokens that are BOTH a registered use case and a real manufacturer. For these
 * the brand reading wins, because a manufacturer's own collection is the more
 * valuable page and the use-case form is still reachable as `{type}-for-{x}`.
 *
 * Derived, not guessed: intersecting the 159 use-case slugs with the 415
 * BRAND_RULES keys gives exactly two — `rv` and `cafe`. Checked against live
 * inventory 08/25/2026:
 *
 *   cafe  Café is a real brand, 31 live products, and /collections/cafe is
 *         INDEXED with 25. It stays a brand.
 *   rv    not a manufacturer. BRAND_RULES carries it only for capitalisation
 *         ("Recreational Vehicle - always uppercase"). It becomes a use case.
 *
 * tests/collection-brand-match.test.ts re-derives that intersection and fails
 * if a third token ever joins it, so a new collision forces a decision instead
 * of silently picking one reading.
 */
const USE_CASE_SLUGS_THAT_ARE_ALSO_BRANDS = new Set(['cafe'])

// Color slugs (must be lowercase, hyphenated)
export const COLOR_SLUGS = [
  'white',
  'black',
  'stainless-steel',
  'black-stainless',
  'slate',
  'bisque',
  'panel-ready',
  'custom-panel',
  'red',
  'green',
  'blue',
  'silver',
  'gray',
  'grey',
  'cream',
  'ivory',
]

// Price range patterns
export const PRICE_RANGES = [
  { slug: 'under-300', maxCents: 30000, display: 'Under $300' },
  { slug: 'under-500', maxCents: 50000, display: 'Under $500' },
  { slug: 'under-750', maxCents: 75000, display: 'Under $750' },
  { slug: 'under-1000', maxCents: 100000, display: 'Under $1,000' },
  { slug: 'under-1500', maxCents: 150000, display: 'Under $1,500' },
  { slug: 'under-2000', maxCents: 200000, display: 'Under $2,000' },
  { slug: 'under-2500', maxCents: 250000, display: 'Under $2,500' },
  { slug: 'under-3000', maxCents: 300000, display: 'Under $3,000' },
  { slug: 'under-4000', maxCents: 400000, display: 'Under $4,000' },
  { slug: 'under-5000', maxCents: 500000, display: 'Under $5,000' },
]

export interface ParsedCollectionUrl {
  // Original slug
  originalSlug: string
  
  // Filters extracted
  brandSlug: string | null
  brandName: string | null
  typeSlug: string | null          // Canonical collection type
  typeName: string | null
  colorSlug: string | null
  colorName: string | null
  priceSlug: string | null         // e.g., 'under-1000'
  priceMaxCents: number | null
  priceDisplay: string | null
  useCaseSlug: string | null       // e.g., 'bedroom'
  useCaseName: string | null
  isEnergyStar: boolean
  
  // Computed
  filterCount: number              // Number of active filters
  displayTitle: string             // Human-readable title for H1/breadcrumb
  metaDescription: string          // Meta description for SEO
  canonicalSlug: string            // For Supabase lookup
  isFiltered: boolean          // Whether this is a filtered collection
}

/**
 * Parse a collection URL slug into its component filters
 */
export function parseCollectionUrl(slug: string): ParsedCollectionUrl {
  const result: ParsedCollectionUrl = {
    originalSlug: slug,
    brandSlug: null,
    brandName: null,
    typeSlug: null,
    typeName: null,
    colorSlug: null,
    colorName: null,
    priceSlug: null,
    priceMaxCents: null,
    priceDisplay: null,
    useCaseSlug: null,
    useCaseName: null,
    isEnergyStar: false,
    filterCount: 0,
    displayTitle: '',
    metaDescription: '',
    canonicalSlug: slug,
    isFiltered: false,
  }
  
  let remaining = slug
  
  // 1. Check for Energy Star suffix
  if (remaining.endsWith('-energy-star')) {
    result.isEnergyStar = true
    remaining = remaining.replace(/-energy-star$/, '')
    result.filterCount++
    result.isFiltered = true
  }
  
  // Handle standalone energy-star collection (and its redirect target energy-star-appliances)
  if (slug === 'energy-star' || slug === 'energy-star-appliances') {
    result.isEnergyStar = true
    result.filterCount = 1
    result.displayTitle = 'Energy Star Appliances'
    result.metaDescription = 'Shop Energy Star refrigerators and freezers at Fridge.com. Save on energy costs with efficient appliances that meet strict EPA guidelines.'
    result.canonicalSlug = 'energy-star-appliances'
    result.isFiltered = true
    return result
  }
  
  // 1b. Check for Energy Star PREFIX pattern: energy-star-{type}
  // e.g., energy-star-freezers, energy-star-refrigerators, energy-star-refrigerators-for-dorm
  if (remaining.startsWith('energy-star-')) {
    result.isEnergyStar = true
    remaining = remaining.replace(/^energy-star-/, '')
    result.filterCount++
    result.isFiltered = true
  }
  
  // 2. Check for price range suffix
  for (const price of PRICE_RANGES) {
    if (remaining.endsWith(`-${price.slug}`)) {
      result.priceSlug = price.slug
      result.priceMaxCents = price.maxCents
      result.priceDisplay = price.display
      remaining = remaining.replace(new RegExp(`-${price.slug}$`), '')
      result.filterCount++
      result.isFiltered = true
      break
    }
  }
  
  // 3. Check for use case pattern: {type}-for-{use-case}
  const forMatch = remaining.match(/^(.+)-for-([a-z0-9-]+)$/)
  if (forMatch) {
    const potentialUseCase = forMatch[2]
    const useCase = getUseCase(potentialUseCase)
    if (useCase) {
      result.useCaseSlug = useCase.slug
      result.useCaseName = useCase.name
      remaining = forMatch[1]
      result.filterCount++
      result.isFiltered = true
    }
  }
  
  // 4. Check for color prefix
  // But first, check if remaining STARTS WITH a known brand slug (not just IS a brand)
  // This handles "black-decker-mini-fridges" where we need to recognize "black-decker" as a brand
  // before the color check tries to match "black" as a color
  
  // Check if full remaining is a known brand first (original check)
  const remainingAsBrand = remaining.replace(/-/g, ' ').toLowerCase()
  let startsWithBrand = BRAND_RULES[remainingAsBrand] !== undefined
  
  // If not a full brand match, check if remaining STARTS WITH a known brand slug
  // This catches "black-decker-mini-fridges" where "black-decker" is a brand prefix
  if (!startsWithBrand) {
    // Known multi-word brand slugs that could be confused with colors
    // These are brands where the first part matches a color (e.g., "black" in "black-decker")
    const brandPrefixes = [
      'black-decker',  // Black+Decker - "black" could match color
      'black-and-decker', // Alternate spelling
      'blue-star',     // BlueStar - "blue" could match color
      'red-bull',      // Red Bull - "red" could match color
      'white-westinghouse', // White-Westinghouse - "white" could match color
    ]
    
    for (const brandPrefix of brandPrefixes) {
      if (remaining === brandPrefix || remaining.startsWith(`${brandPrefix}-`)) {
        startsWithBrand = true
        break
      }
    }
  }
  
  // Only check for color prefix if remaining does NOT start with a brand
  if (!startsWithBrand) {
    // Longest match first — the same discipline the canonical-type loop below
    // already applies. Array order once decided this: 'black' sat before
    // 'black-stainless', so black-stainless-refrigerators parsed as
    // colour=black plus an unresolvable remainder and rendered ZERO of its
    // 103 tagged products, which then got the collection auto-gated
    // (FRIDGE-259). Pinned by tests/collection-url-parser-color.test.ts.
    const colorsLongestFirst = [...COLOR_SLUGS].sort((a, b) => b.length - a.length)
    for (const color of colorsLongestFirst) {
      if (remaining.startsWith(`${color}-`)) {
        result.colorSlug = color
        result.colorName = color.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        remaining = remaining.replace(new RegExp(`^${color}-`), '')
        result.filterCount++
        result.isFiltered = true
        break
      }
    }
  }
  
  // 5. Check for canonical collection type (longest match first)
  const canonicalSlugs = allCanonicalSlugs().sort((a, b) => b.length - a.length)
  for (const canon of canonicalSlugs) {
    if (remaining === canon) {
      result.typeSlug = canon
      result.typeName = formatTypeName(canon)
      remaining = ''
      result.filterCount++
      break
    }
    if (remaining.endsWith(`-${canon}`)) {
      result.typeSlug = canon
      result.typeName = formatTypeName(canon)
      remaining = remaining.replace(new RegExp(`-${canon}$`), '')
      result.filterCount++
      break
    }
  }
  
  // 6. What's left should be the brand — UNLESS it is a registered use case.
  //
  // FRIDGE-521. This step used to assign whatever remained as the brand with no
  // check that it IS one, so `{use-case}-{type}` parsed as a brand collection.
  // Step 3 above only recognises the mirror-image form `{type}-for-{use-case}`.
  //
  // What that cost, measured live 08/25/2026: `/collections/rv-refrigerators`
  // (indexed, 48 products) and `/collections/rv-freezers` (indexed, 11) — pages
  // about refrigeration for a recreational vehicle — were populated ENTIRELY by
  // brands whose names happen to contain the letters r-v: Marvel Refrigeration,
  // Marvel, Garveehome, Conserv, Conservator, Garveelife. Not one RV product on
  // either page. `rv` reached this step as a brand because BRAND_RULES carries
  // `'rv': 'RV'` for CAPITALISATION — its own comment says "Recreational
  // Vehicle - always uppercase" — so a brand-name lookup cannot distinguish it
  // from a manufacturer. The use-case registry can, and does.
  //
  // Scope, audited with the real parser over all 50,611 active collections:
  // 5,620 resolve a brandSlug matching no live brand, and exactly TWO of them
  // are indexed — the two above. Everything else is already gated at 0
  // products, i.e. brand pages for manufacturers we do not carry, correctly
  // hidden. So this changes two live pages and nothing else.
  if (remaining && remaining.length > 0 && !USE_CASE_SLUGS_THAT_ARE_ALSO_BRANDS.has(remaining)) {
    const leadingUseCase = getUseCase(remaining)
    if (leadingUseCase) {
      result.useCaseSlug = leadingUseCase.slug
      result.useCaseName = leadingUseCase.name
      result.filterCount++
      result.isFiltered = true
      remaining = ''
    }
  }

  if (remaining && remaining.length > 0) {
    result.brandSlug = remaining
    const brandKey = remaining.replace(/-/g, ' ').toLowerCase()
    result.brandName = BRAND_RULES[brandKey] || remaining.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    result.filterCount++
  }
  
  // 7. Build display title
  result.displayTitle = buildDisplayTitle(result)
  
  // 8. Generate meta description
  result.metaDescription = buildMetaDescription(result)
  
  // 9. Set canonical slug for database lookup
  result.canonicalSlug = result.typeSlug || slug
  
  // 10. Mark as filtered if multiple filters
  if (result.filterCount > 1) {
    result.isFiltered = true
  }
  
  return result
}

/**
 * Format a type slug into a display name
 */
function formatTypeName(slug: string): string {
  return slug
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Generate a display title from parsed filters
 * Order: Color + Brand + Type + Price + Energy Star + Use Case
 */
function buildDisplayTitle(parsed: ParsedCollectionUrl): string {
  const parts: string[] = []
  
  // For Energy Star collections, put "Energy Star" at the beginning
  // e.g., "Energy Star Freezers" not "Freezers Energy Star"
  if (parsed.isEnergyStar && parsed.filterCount > 0) parts.push('Energy Star')
  if (parsed.colorName) parts.push(parsed.colorName)
  if (parsed.brandName) parts.push(parsed.brandName)
  if (parsed.typeName) parts.push(parsed.typeName)
  if (parsed.priceDisplay) parts.push(parsed.priceDisplay)
  
  let title = parts.join(' ') || 'Products'
  
  if (parsed.useCaseName) {
    title = `${title} For ${parsed.useCaseName}`
  }
  
  return title
}

/**
 * Generate a meta description from parsed filters
 */
function buildMetaDescription(parsed: ParsedCollectionUrl): string {
  const parts: string[] = []
  
  if (parsed.colorName) parts.push(parsed.colorName.toLowerCase())
  if (parsed.brandName) parts.push(parsed.brandName)
  if (parsed.typeName) parts.push(parsed.typeName.toLowerCase())
  if (parsed.priceDisplay) parts.push(`priced ${parsed.priceDisplay.toLowerCase()}`)
  if (parsed.isEnergyStar) parts.push('Energy Star certified')
  
  let desc = `Shop ${parts.join(' ') || 'refrigerators and freezers'}`
  
  if (parsed.useCaseName) {
    desc = `${desc} perfect for ${parsed.useCaseName.toLowerCase()}`
  }
  
  return `${desc}. Compare prices, read reviews, and find the best deals | Fridge.com`
}

/**
 * Build a collection URL from filter parameters
 */
export function buildCollectionUrl(params: {
  brand?: string
  type?: string
  color?: string
  priceRange?: string
  useCase?: string
  energyStar?: boolean
}): string {
  const parts: string[] = []
  
  if (params.color) parts.push(params.color)
  if (params.brand) parts.push(params.brand)
  if (params.type) parts.push(params.type)
  if (params.useCase) parts.push(`for-${params.useCase}`)
  if (params.priceRange) parts.push(params.priceRange)
  if (params.energyStar) parts.push('energy-star')
  
  return `/collections/${parts.join('-')}`
}

/**
 * Get price range by slug
 */
export function getPriceRange(slug: string): typeof PRICE_RANGES[0] | null {
  return PRICE_RANGES.find(p => p.slug === slug) || null
}

/**
 * Check if a slug is a valid color
 */
export function isColorSlug(slug: string): boolean {
  return COLOR_SLUGS.includes(slug)
}

/**
 * Check if a slug matches a price range pattern
 */
export function isPriceRangeSlug(slug: string): boolean {
  return PRICE_RANGES.some(p => p.slug === slug)
}

